import { Button, PopoverProps, Typography } from '@mui/material';
import React, { cloneElement, FC, ReactElement, useId, useState } from 'react';

import { dialogMap } from '../../../constants/dialog';
import translate from '../../../i18n/translate';
import { DialogId } from '../../../models/dialog';

import { ExternalPopoverProps } from '../Popover';

import * as Styled from './styled';

type Props = {
  onConfirm: () => void;
  popoverProps?: Partial<PopoverProps>;
  shouldConfirm?: boolean;
  trigger: ReactElement;
  type: DialogId;
};

/**
 * Confirm popover component for notifying user about certain action
 *
 * @param props               Props passed to the component
 * @param props.onConfirm     Callback triggered when action is confirmed
 * @param props.popoverProps  Popover props
 * @param props.shouldConfirm Whether to show the confirmation dialog
 * @param props.trigger       The element that opens the popover
 * @param props.type          Type of the popover (id of the dialog)
 *
 * @returns                   Component itself
 */
const ConfirmPopover: FC<Props> = ({
  onConfirm,
  popoverProps,
  shouldConfirm = true,
  trigger,
  type,
}) => {
  const descriptionId = useId();
  const titleId = useId();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (shouldConfirm === false) {
    return cloneElement(trigger, { onClick: onConfirm });
  }

  const popoverHandlers: ExternalPopoverProps = {
    anchorEl,
    onStateChange: setAnchorEl,
  };

  /**
   * Close the dialog
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Confirm action in dialog and close the dialog
   */
  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };

  const { cancelLabel, confirmLabel, description, title } = dialogMap[type];

  return (
    <Styled.StyledPopover
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'bottom',
      }}
      externalHandlerProps={popoverHandlers}
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
      trigger={trigger}
      {...(popoverProps !== undefined && popoverProps)}
    >
      <Styled.Wrapper
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
      >
        <Styled.DialogTitle id={titleId}>{translate(title)}</Styled.DialogTitle>
        <Typography component="section" id={descriptionId} variant="body1">
          {translate(description)}
        </Typography>
        <Styled.DialogActions>
          <Button onClick={handleClose} variant="outlined">
            {translate(cancelLabel)}
          </Button>
          <Button color="error" onClick={handleConfirm} variant="outlined">
            {translate(confirmLabel)}
          </Button>
        </Styled.DialogActions>
      </Styled.Wrapper>
    </Styled.StyledPopover>
  );
};

export default ConfirmPopover;
